import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TimeTrackingService } from '../../services/time-tracking.service';
import { AuthService } from '../../services/auth.service';
import { TimeOffRequest } from '../../interfaces/time-tracking.interface';
import { UserProfile } from '../../interfaces/user-profile.interface';
import { Observable, combineLatest } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

interface CalendarDay {
  date: Date;
  timeOffRequests: TimeOffRequest[];
  isToday: boolean;
  isCurrentMonth: boolean;
  timeOffSpans: TimeOffSpan[];
}

interface TimeOffSpan {
  request: TimeOffRequest;
  isStart: boolean;
  isEnd: boolean;
  isMiddle: boolean;
}

// Add this function at the top level, outside the component
function generateUserColor(userId: string): string {
  // Generate a consistent color based on the user ID
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = userId.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  // Convert to HSL to ensure good contrast and consistent saturation/lightness
  const hue = hash % 360;
  return `hsl(${hue}, 70%, 40%)`;
}

@Component({
  selector: 'app-vacation-calendar',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './vacation-calendar.component.html',
  styleUrls: ['./vacation-calendar.component.scss']
})
export class VacationCalendarComponent implements OnInit {
  currentDate = new Date();
  calendarDays$: Observable<CalendarDay[]>;
  users: UserProfile[] = [];
  selectedUser: UserProfile | null = null;
  showUserDropdown = false;

  constructor(
    private timeTrackingService: TimeTrackingService,
    private authService: AuthService
  ) {
    this.calendarDays$ = this.generateCalendarDays();
  }

  ngOnInit() {
    this.timeTrackingService.getAllUsers().subscribe(users => {
      this.users = users;
    });
  }

  previousMonth() {
    this.currentDate = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() - 1, 1);
    this.calendarDays$ = this.generateCalendarDays();
  }

  nextMonth() {
    this.currentDate = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() + 1, 1);
    this.calendarDays$ = this.generateCalendarDays();
  }

  toggleUserDropdown() {
    this.showUserDropdown = !this.showUserDropdown;
  }

  selectUser(user: UserProfile | null) {
    this.selectedUser = user;
    this.showUserDropdown = false;
    this.calendarDays$ = this.generateCalendarDays();
  }

  getUserColor(userId: string): string {
    return generateUserColor(userId);
  }

  getUserName(userId: string): string {
    const user = this.users.find(u => u.uid === userId);
    return user ? `${user.firstName} ${user.lastName}` : 'Unknown User';
  }

  private generateCalendarDays(): Observable<CalendarDay[]> {
    return this.timeTrackingService.getApprovedTimeOffRequests().pipe(
      map(requests => {
        const filteredRequests = requests.filter(request => 
          !this.selectedUser || request.userId === this.selectedUser.uid
        );

        const firstDay = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth(), 1);
        const lastDay = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() + 1, 0);
        
        // Add days from previous month
        const daysInCalendar: CalendarDay[] = [];
        const startPadding = firstDay.getDay();
        for (let i = startPadding - 1; i >= 0; i--) {
          const date = new Date(firstDay);
          date.setDate(date.getDate() - i - 1);
          daysInCalendar.push(this.createCalendarDay(date, filteredRequests));
        }

        // Add days of current month
        for (let i = 1; i <= lastDay.getDate(); i++) {
          const date = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth(), i);
          daysInCalendar.push(this.createCalendarDay(date, filteredRequests));
        }

        // Add days from next month
        const endPadding = (7 - ((daysInCalendar.length) % 7)) % 7;
        for (let i = 1; i <= endPadding; i++) {
          const date = new Date(lastDay);
          date.setDate(date.getDate() + i);
          daysInCalendar.push(this.createCalendarDay(date, filteredRequests));
        }

        return daysInCalendar;
      })
    );
  }

  private createCalendarDay(date: Date, requests: TimeOffRequest[]): CalendarDay {
    const today = new Date();
    const isToday = date.getDate() === today.getDate() &&
                    date.getMonth() === today.getMonth() &&
                    date.getFullYear() === today.getFullYear();

    // Normalize the comparison date to midnight
    const normalizedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    const timeOffRequests = requests.filter(request => {
      // Normalize start and end dates to midnight
      const startDate = new Date(request.startDate);
      startDate.setHours(0, 0, 0, 0);
      const endDate = new Date(request.endDate);
      endDate.setHours(0, 0, 0, 0);
      
      return normalizedDate >= startDate && normalizedDate <= endDate;
    });

    const timeOffSpans = timeOffRequests.map(request => {
      const startDate = new Date(request.startDate);
      startDate.setHours(0, 0, 0, 0);
      const endDate = new Date(request.endDate);
      endDate.setHours(0, 0, 0, 0);
      
      return {
        request,
        isStart: normalizedDate.getTime() === startDate.getTime(),
        isEnd: normalizedDate.getTime() === endDate.getTime(),
        isMiddle: normalizedDate > startDate && normalizedDate < endDate
      };
    });

    return {
      date,
      timeOffRequests,
      isToday,
      isCurrentMonth: date.getMonth() === this.currentDate.getMonth(),
      timeOffSpans
    };
  }
} 